import { create } from 'zustand';
import axios from 'axios';

const hasMeaningfulText = (value) => typeof value === 'string' && value.trim().length > 0;

const normalizeTextKey = (value) => String(value || '').trim();

const fetchBatchTranslatedTexts = async (lang, texts, accessToken) => {
    const hasValidToken = !!accessToken?.trim();
    const hasTextsToTranslate = texts && texts.length > 0;

    if (!hasValidToken || !hasTextsToTranslate) {
        return [];
    }

    let version;
    try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_FASTIFY_API_URL}/v1/version`,
        );
        version = data.version;
    } catch (error) {
        console.error(`Error fetching version: ${error.message}`);
        return [];
    }
    try {
        const translationRequests = texts.map((text) => ({ text, targetLang: lang }));
        const response = await axios.post(
            `${process.env.REACT_APP_FASTIFY_API_URL}/${version}/translate/batch`,
            { translations: translationRequests },
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            }
        );
        console.log("response from translation routes: ", response);
        return response.data.translations; // Array of translated objects
    } catch (error) {
        console.error('Batch translation error:', error);
        throw error;
    }
};

const useAppStore = create((set, get) => ({
    testMode: false,
    lang: "",
    setLang: async (newLang) => {
        set({ lang: newLang, tLoading: true });
        const { originalQuestions } = get();
        await get().setTranslatedQuestions(originalQuestions);
    },
    accessToken: '',
    setAccessToken: (newAccessToken) => set({ accessToken: newAccessToken }),
    setTestMode: (newTestMode) => set({ testMode: newTestMode }),
    
    questions: [],
    originalQuestions: [],
    setOriginalQuestions: (newQuestions) => {
        console.log("Setting original questions:", newQuestions);
        set({ originalQuestions: newQuestions, questions: newQuestions });
    },
    setTranslatedQuestions: async (newQuestions, inputLang = "") => {
        const { lang, accessToken } = get();
        set({ tLoading: true });
        const targetLang = inputLang || lang;

        const hasValidToken = !!accessToken?.trim();
        const hasQuestions = newQuestions && newQuestions.length > 0;

        if (!hasValidToken || !hasQuestions) {
            set({ questions: newQuestions || [], tLoading: false });
            return;
        }

        // Build translation list while skipping empty/whitespace strings.
        const translationTargets = [];
        newQuestions.forEach((question, questionIndex) => {
            if (hasMeaningfulText(question.title)) {
                translationTargets.push({ questionIndex, field: 'title', text: question.title });
            }
            if (hasMeaningfulText(question.name)) {
                translationTargets.push({ questionIndex, field: 'name', text: question.name });
            }
        });

        if (translationTargets.length === 0) {
            set({ questions: newQuestions, tLoading: false });
            return;
        }

        try {
            const translatedTexts = await fetchBatchTranslatedTexts(
                targetLang,
                translationTargets.map((item) => item.text),
                accessToken
            );

            const translationsBySourceText = new Map();
            translatedTexts.forEach((translation) => {
                const sourceText = normalizeTextKey(translation?.text);
                if (!sourceText) return;

                const existingTranslations = translationsBySourceText.get(sourceText) || [];
                existingTranslations.push(translation);
                translationsBySourceText.set(sourceText, existingTranslations);
            });

            const translatedQuestions = newQuestions.map((question) => ({ ...question }));

            translationTargets.forEach((target, index) => {
                const sourceText = normalizeTextKey(target.text);
                const matchingTranslations = translationsBySourceText.get(sourceText) || [];
                const matchedTranslation = matchingTranslations.shift();

                if (matchedTranslation) {
                    translationsBySourceText.set(sourceText, matchingTranslations);
                }

                const indexCandidate = translatedTexts[index];
                const indexCandidateMatchesSource =
                    !hasMeaningfulText(indexCandidate?.text) ||
                    normalizeTextKey(indexCandidate.text) === sourceText;

                const candidate = matchedTranslation?.translatedText ||
                    (indexCandidateMatchesSource ? indexCandidate?.translatedText : null);

                translatedQuestions[target.questionIndex][target.field] =
                    hasMeaningfulText(candidate) ? candidate : target.text;
            });

            set({ questions: translatedQuestions, tLoading: false });
        } catch (error) {
            console.error('setTranslatedQuestions fallback:', error);
            set({ questions: newQuestions, tLoading: false });
        }
    },
    isMobile: window.innerWidth <= 768,
    setIsMobile: (newIsMobile) => set({ isMobile: newIsMobile }),
    getIsMobile: () => get().isMobile,
}));

export default useAppStore;
